import type {
  ApprovalChatRequest,
  ApprovalProposalListParams,
  ApprovalSubmitRequest,
} from "@adpilot/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HTTPError, TimeoutError } from "ky";
import {
  fetchApprovalProposalDetail,
  fetchApprovalProposals,
  requestProposalRevision,
  submitApprovalProposal,
} from "@/lib/approval-api";
import { STALE_TIME } from "@/lib/query-client";

export const approvalQueryKeys = {
  all: ["approvals"] as const,
  list: (params: ApprovalProposalListParams) => ["approvals", "list", params] as const,
  detail: (proposalId: string) => ["approvals", "detail", proposalId] as const,
};

interface ApprovalQueryInvalidator {
  invalidateQueries(options: { queryKey: readonly unknown[] }): Promise<unknown>;
}

export async function invalidateApprovalData(queryClient: ApprovalQueryInvalidator): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: approvalQueryKeys.all }),
    queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
  ]);
}

export function shouldRetryApprovalMutation(failureCount: number, error: Error): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof HTTPError) return error.response.status >= 500;
  return error instanceof TimeoutError || error instanceof TypeError;
}

export function useApprovalProposals(params: ApprovalProposalListParams) {
  return useQuery({
    queryKey: approvalQueryKeys.list(params),
    queryFn: () => fetchApprovalProposals(params),
    staleTime: STALE_TIME,
  });
}

export function useApprovalProposalDetail(
  proposalId: string | undefined,
  pollWhileRunActive = false,
  // 运行详情页跟随资源店铺(聚合视角下 ≠ 当前激活店铺),显式带上。
  shopId?: string,
) {
  return useQuery({
    queryKey: approvalQueryKeys.detail(proposalId ?? ""),
    queryFn: () => fetchApprovalProposalDetail(proposalId!, shopId),
    enabled: proposalId !== undefined,
    staleTime: STALE_TIME,
    refetchInterval: (query) =>
      pollWhileRunActive || query.state.data?.data.finalizationStatus === "processing"
        ? 3_000
        : false,
  });
}

export interface ApprovalMutationVariables<TInput> {
  input: TInput;
  idempotencyKey: string;
}

export function createApprovalMutationVariables<TInput>(
  input: TInput,
  createKey: () => string = () => crypto.randomUUID(),
): ApprovalMutationVariables<TInput> {
  return { input, idempotencyKey: createKey() };
}

export function executeApprovalMutation<TInput, TData>(
  request: (input: TInput, idempotencyKey: string) => Promise<TData>,
  variables: ApprovalMutationVariables<TInput>,
): Promise<TData> {
  return request(variables.input, variables.idempotencyKey);
}

function useApprovalDecisionMutation<TInput, TData>(
  request: (input: TInput, idempotencyKey: string) => Promise<TData>,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation<TData, Error, ApprovalMutationVariables<TInput>>({
    mutationFn: (variables) => executeApprovalMutation(request, variables),
    // A retry receives the same internal variables, including the key created
    // by mutate/mutateAsync below. The backend therefore sees one command.
    retry: shouldRetryApprovalMutation,
    onSuccess: () => invalidateApprovalData(queryClient),
  });
  const { mutate, mutateAsync, ...state } = mutation;
  return {
    ...state,
    mutate: (input: TInput) => mutate(createApprovalMutationVariables(input)),
    mutateAsync: (input: TInput) => mutateAsync(createApprovalMutationVariables(input)),
  };
}

interface ProposalDecisionInput {
  proposalId: string;
  /** 资源所属店铺;聚合视角下必传,否则请求头落到当前激活店铺导致 404。 */
  shopId?: string;
}

interface SubmitDecisionInput extends ProposalDecisionInput {
  body: ApprovalSubmitRequest;
}

export function useSubmitApprovalProposal() {
  return useApprovalDecisionMutation(({ proposalId, body, shopId }: SubmitDecisionInput, key) =>
    submitApprovalProposal(proposalId, body, key, shopId),
  );
}

interface RevisionDecisionInput extends ProposalDecisionInput {
  body: ApprovalChatRequest;
}

export function useRequestProposalRevision() {
  return useApprovalDecisionMutation(({ proposalId, body, shopId }: RevisionDecisionInput, key) =>
    requestProposalRevision(proposalId, body, key, shopId),
  );
}
